import { Box, CircularProgress, Grid, Paper, Typography } from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import React from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend, ArcElement,
} from 'chart.js';
import {Line, Doughnut} from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
);

// Fixed palette (no random) for the topic doughnut
const DOUGHNUT_BG = [
    'rgba(99, 102, 241, 0.6)',
    'rgba(139, 92, 246, 0.6)',
    'rgba(6, 182, 212, 0.6)',
    'rgba(16, 185, 129, 0.6)',
    'rgba(245, 158, 11, 0.6)',
    'rgba(239, 68, 68, 0.6)',
    'rgba(148, 163, 184, 0.6)',
];
const DOUGHNUT_BORDER = [
    'rgba(99, 102, 241, 1)',
    'rgba(139, 92, 246, 1)',
    'rgba(6, 182, 212, 1)',
    'rgba(16, 185, 129, 1)',
    'rgba(245, 158, 11, 1)',
    'rgba(239, 68, 68, 1)',
    'rgba(148, 163, 184, 1)',
];

const StatTile = ({ label, value }) => (
    <Paper
        elevation={0}
        sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: '12px',
            border: '1px solid rgba(226,232,240,0.8)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
        }}
    >
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#6366f1', lineHeight: 1.2 }}>
            {value}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(71,85,105,0.8)', mt: 0.5 }}>
            {label}
        </Typography>
    </Paper>
);

const AnalyticsMainPageContent = ({ isLoading, analytics }) => {

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: false
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: { precision: 0 }
            }
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
                <CircularProgress sx={{ color: '#6366f1' }} />
            </Box>
        );
    }

    const {
        totalRatings = 0,
        distinctTopics = 0,
        distinctCollections = 0,
        avgStars = 0,
        monthLabels = [],
        monthlyCounts = [],
        tagLabels = [],
        tagValues = [],
    } = analytics || {};

    // Empty state - user has no ratings yet
    if (!totalRatings) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: { xs: 8, sm: 12 },
                px: { xs: 2, sm: 4 },
                textAlign: 'center'
            }}>
                <Box sx={{
                    width: { xs: 80, sm: 100 },
                    height: { xs: 80, sm: 100 },
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.1) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3
                }}>
                    <InsightsOutlinedIcon sx={{ fontSize: { xs: 40, sm: 50 }, color: 'rgba(99,102,241,0.6)' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'rgba(30,41,59,0.9)', mb: 1.5 }}>
                    No analytics yet
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(71,85,105,0.7)', maxWidth: '500px', lineHeight: 1.6 }}>
                    Start rating items and your activity trends will show up here.
                </Typography>
            </Box>
        );
    }

    const lineData = {
        labels: monthLabels,
        datasets: [
            {
                label: 'Ratings per month',
                data: monthlyCounts,
                borderColor: 'rgb(99, 102, 241)',
                backgroundColor: 'rgba(99, 102, 241, 0.5)',
                tension: 0.3,
            },
        ],
    };

    const hasTopics = tagLabels.length > 0;
    const doughnutData = {
        labels: tagLabels,
        datasets: [
            {
                label: 'Ratings by topic',
                data: tagValues,
                backgroundColor: DOUGHNUT_BG.slice(0, tagLabels.length),
                borderColor: DOUGHNUT_BORDER.slice(0, tagLabels.length),
                borderWidth: 1,
            },
        ],
    };

    return (
        <div style={{ padding: '0 8px' }}>
            {/* Real summary stats derived from the user's ratings */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <StatTile label="Ratings made" value={totalRatings} />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatTile label="Topics covered" value={distinctTopics} />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatTile label="Collections" value={distinctCollections} />
                </Grid>
                <Grid item xs={6} sm={3}>
                    <StatTile label="Avg. stars" value={avgStars ? avgStars.toFixed(1) : '-'} />
                </Grid>
            </Grid>

            <h3 style={{
                fontSize: window.innerWidth < 600 ? '1.25rem' : '1.5rem',
                marginBottom: '16px'
            }}>
                Ratings over time
            </h3>
            <div style={{ marginBottom: '32px' }}>
                <Line options={options} data={lineData} />
            </div>

            <h3 style={{
                fontSize: window.innerWidth < 600 ? '1.25rem' : '1.5rem',
                marginBottom: '16px'
            }}>
                Ratings by topic
            </h3>
            {hasTopics ? (
                <div style={{
                    width: '100%',
                    maxWidth: '400px',
                    margin: '0 auto'
                }}>
                    <Doughnut data={doughnutData} />
                </div>
            ) : (
                <Typography variant="body2" sx={{ color: 'rgba(71,85,105,0.7)', textAlign: 'center', py: 3 }}>
                    Add topics/tags to your ratings to see this breakdown.
                </Typography>
            )}
        </div>
    );
};

export default AnalyticsMainPageContent;
